/**
 * Mobile product grid using stacked-slide carousel cards (no translate = no seam).
 * Same fetch/infinite-scroll as Slider.js but renders ProductCardCarouselStack.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ProductCardCarouselStack } from '../Components/images/Mob/ProductCardCarouselStack';
import { ProductCardTranslateCarousel } from '../Components/images/Mob/ProductCardTranslateCarousel';
import { SimpleTouchCarouselCard } from '../Products/Mobile/SimpleTouchCarouselCard';
import { ProductGridSkeleton } from '../Components/images/Mob/ProductGridSkeleton';
import { useContextData } from '../ApiContext';
import { ensureMediaUrl } from '../utils/mediaUrl';
import './SliderMobNew.scss';

function queryParamsEqual(a, b) {
    if (!a || !b) return false;
    const toArr = (obj, key) => (Array.isArray(obj?.[key]) ? obj[key].slice().sort() : []);
    return (
        JSON.stringify(toArr(a, 'category')) === JSON.stringify(toArr(b, 'category')) &&
        JSON.stringify(toArr(a, 'subcategory')) === JSON.stringify(toArr(b, 'subcategory')) &&
        JSON.stringify(toArr(a, 'caseType')) === JSON.stringify(toArr(b, 'caseType')) &&
        JSON.stringify(toArr(a, 'brand')) === JSON.stringify(toArr(b, 'brand')) &&
        JSON.stringify(toArr(a, 'color')) === JSON.stringify(toArr(b, 'color')) &&
        JSON.stringify(a.price || [0, 10000]) === JSON.stringify(b.price || [0, 10000]) &&
        (a.sort || 'popularity') === (b.sort || 'popularity')
    );
}

const SCROLL_STORAGE_KEY_PREFIX = 'productsListScroll_';
const DEBUG_SCROLL_RESTORE = true; // set to false to disable scroll debug logs
const DEBUG_SCROLL_SAVE = true; // set to false to disable save-side logs

/** Expand intersection box upward so the next page loads before the user hits the very bottom. */
const INFINITE_SCROLL_ROOT_MARGIN = '0px 0px 220px 0px';

/** Must match API request `size` — used to detect “all results on first page” for mobile UI. */
export const MOB_PRODUCTS_PAGE_SIZE = 10;

export function SliderMobNew({
    url,
    queryParams,
    queryParamsInit,
    onFirstPageLoadMeta,
    mobileCardVariant = 'stacked',
    /** Pass through to simple-touch / translate-strip cards — click image to start carousel (e.g. DevTools). */
    mobPreviewClickActivatesCarousel = false,
}) {
    const location = useLocation();
    const { productListCache, setProductListCache } = useContextData();
    const restoredFromCacheRef = useRef(false);
    const restoreScrollRef = useRef(false);
    const lastScrollYRef = useRef(0);
    const productListCacheRef = useRef(productListCache);
    productListCacheRef.current = productListCache;
    const slidesLengthRef = useRef(0);
    const gridRef = useRef(null);
    const activateRafRef = useRef(null);

    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startRef, setStartRef] = useState(false);
    const [page, setPage] = useState(1);
    const [noMoreProducts, setNoMoreProducts] = useState(false);
    const [activeCardId, setActiveCardId] = useState(null);
    const observer = useRef();
    const onFirstPageLoadMetaRef = useRef(onFirstPageLoadMeta);
    onFirstPageLoadMetaRef.current = onFirstPageLoadMeta;

    useEffect(() => {
        slidesLengthRef.current = slides.length;
    }, [slides.length]);

    /**
     * iOS Safari often defers final compositing of many new <img> layers on cold load.
     * A later scroll/touch “fixes” it; we approximate that by nudging layout + decode
     * after the grid paints (double rAF + delayed passes).
     */
    useEffect(() => {
        if (loading || slides.length === 0) return;

        const nudge = () => {
            const root = gridRef.current;
            if (!root) return;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const el = gridRef.current;
                    if (!el) return;
                    void el.offsetHeight;
                    el.querySelectorAll(
                        'img.product-card-stack__media-img, img.simple-touch-card__img, img.product-card-translate__media-img'
                    ).forEach((img, idx) => {
                        if (idx >= 20) return;
                        if (typeof img.decode === 'function') {
                            img.decode().catch(() => {});
                        }
                    });
                });
            });
        };

        nudge();
        const t1 = window.setTimeout(nudge, 64);
        const t2 = window.setTimeout(nudge, 200);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
    }, [loading, slides.length]);

    useEffect(() => {
        return () => {
            if (observer.current) {
                observer.current.disconnect();
                observer.current = null;
            }
            if (activateRafRef.current !== null) {
                window.cancelAnimationFrame(activateRafRef.current);
                activateRafRef.current = null;
            }
        };
    }, []);

    const handleActivateCard = useCallback((id) => {
        if (activateRafRef.current !== null) {
            window.cancelAnimationFrame(activateRafRef.current);
        }
        activateRafRef.current = window.requestAnimationFrame(() => {
            setActiveCardId((prev) => (String(prev) === String(id) ? prev : id));
            activateRafRef.current = null;
        });
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.post(
                url || '/products',
                { ...queryParams, page, size: MOB_PRODUCTS_PAGE_SIZE },
                config
            );
            const data = response.data || [];
            if (!data.length || data.length < MOB_PRODUCTS_PAGE_SIZE) setNoMoreProducts(true);
            if (page === 1) {
                onFirstPageLoadMetaRef.current?.({
                    exhaustedOnFirstPage: !data.length || data.length < MOB_PRODUCTS_PAGE_SIZE,
                });
            }
            if (data.length) {
                const newSlides = data
                    .filter((item) => item.urls && item.urls.length > 0)
                    .map((item) => ({
                        id: item._id,
                        urls: item.urls.map((u) => ensureMediaUrl(u)),
                        title: item.title,
                        price: item.price,
                        brand: item.brand,
                        mrp: item.mrp,
                        rating: item.rating,
                        reviewCount: item.reviewCount,
                        isNew: item.isNew,
                    }));
                setSlides((prev) => {
                    const existingIds = new Set(prev.map((s) => String(s.id)));
                    const toAppend = newSlides.filter((s) => !existingIds.has(String(s.id)));
                    return toAppend.length ? [...prev, ...toAppend] : prev;
                });
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
            if (page === 1) {
                onFirstPageLoadMetaRef.current?.({ exhaustedOnFirstPage: false });
            }
        } finally {
            setLoading(false);
            setStartRef(true);
        }
    };

    useEffect(() => {
        const cache = productListCacheRef.current;
        const cacheMatches =
            cache &&
            cache.pathname === location.pathname &&
            queryParamsEqual(cache.queryParams, queryParams);
        if (cacheMatches && Array.isArray(cache.slides) && cache.slides.length > 0) {
            restoredFromCacheRef.current = true;
            restoreScrollRef.current = true;
            const cachedPage = cache.page ?? 1;
            const cachedNoMore =
                cache.noMoreProducts ??
                (cachedPage === 1 && cache.slides.length < MOB_PRODUCTS_PAGE_SIZE);
            setSlides(cache.slides);
            setPage(cachedPage);
            setNoMoreProducts(!!cachedNoMore);
            setStartRef(true);
            const exhaustedOnFirstPage = cachedPage === 1 && !!cachedNoMore;
            onFirstPageLoadMetaRef.current?.({ exhaustedOnFirstPage });
            return;
        }
        setNoMoreProducts(false);
        setSlides([]);
        setPage(1);
    }, [queryParams, location.pathname]);

    useEffect(() => {
        const onScroll = () => {
            lastScrollYRef.current = window.scrollY;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (!restoreScrollRef.current || slides.length === 0) return;
        const key = SCROLL_STORAGE_KEY_PREFIX + location.pathname;
        const saved = sessionStorage.getItem(key);
        if (DEBUG_SCROLL_RESTORE) {
            console.log('[SliderMobNew] RESTORE: attempt', {
                key,
                savedRaw: saved,
                slidesLength: slides.length,
                pathname: location.pathname,
                currentScrollYBefore: window.scrollY,
                docScrollHeight: document.documentElement.scrollHeight,
                viewportH: window.innerHeight,
            });
        }
        if (saved !== null) {
            let y = parseInt(saved, 10);
            if (!Number.isNaN(y) && y > 0) {
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                const target = Math.min(y, Math.max(0, maxScroll));
                if (DEBUG_SCROLL_RESTORE) {
                    console.log('[SliderMobNew] RESTORE: saved position from storage:', y, 'key:', key, 'capped target:', target, 'maxScroll:', maxScroll);
                }
                const scrollToY = () => {
                    const max = document.documentElement.scrollHeight - window.innerHeight;
                    const t = Math.min(y, Math.max(0, max));
                    window.scrollTo(0, t);
                    if (DEBUG_SCROLL_RESTORE) {
                        console.log('[SliderMobNew] RESTORE: position after scroll:', window.scrollY);
                    }
                };
                requestAnimationFrame(() => {
                    scrollToY();
                    requestAnimationFrame(scrollToY);
                });
                setTimeout(scrollToY, 100);
                setTimeout(() => {
                    scrollToY();
                    if (DEBUG_SCROLL_RESTORE) {
                        console.log('[SliderMobNew] RESTORE: final position after coming back:', window.scrollY);
                    }
                }, 300);
            }
            sessionStorage.removeItem(key);
        }
        restoreScrollRef.current = false;
    }, [slides.length, location.pathname]);

    useEffect(() => {
        const path = location.pathname;
        return () => {
            const key = SCROLL_STORAGE_KEY_PREFIX + path;
            const positionAtClick = lastScrollYRef.current;
            try {
                // Prevent overwriting a good saved position with a "0" value that can
                // appear during route transitions / React StrictMode dev unmounts.
                const shouldSave = slidesLengthRef.current > 0 && positionAtClick > 0;
                if (shouldSave) {
                    sessionStorage.setItem(key, String(positionAtClick));
                }
                if (DEBUG_SCROLL_SAVE) {
                    console.log('[SliderMobNew] SAVE (on leave):', {
                        key,
                        positionAtClick,
                        windowScrollY: window.scrollY,
                        slidesLength: slidesLengthRef.current,
                        pathname: path,
                        shouldSave,
                    });
                }
            } catch (e) {
                // ignore storage errors
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        if (!queryParamsInit) return;
        if (restoredFromCacheRef.current) {
            restoredFromCacheRef.current = false;
            return;
        }
        fetchProducts();
    }, [page, queryParams, queryParamsInit]);

    useEffect(() => {
        if (!queryParamsInit || slides.length === 0) return;
        setProductListCache({
            pathname: location.pathname,
            queryParams: { ...queryParams },
            slides: [...slides],
            page,
            noMoreProducts,
        });
    }, [slides, page, queryParams, location.pathname, queryParamsInit]);

    const lastProductRef = useCallback((node) => {
        if (observer.current) {
            observer.current.disconnect();
            observer.current = null;
        }
        if (loading || noMoreProducts || !startRef || !node) return;

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setPage((p) => p + 1);
                }
            },
            {
                root: null,
                rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
                threshold: 0,
            }
        );
        observer.current.observe(node);
    }, [loading, noMoreProducts, startRef]);

    const showInitialSkeleton =
        queryParamsInit && slides.length === 0 && (loading || !startRef);

    return (
        <div className="slider-mob-new">
            {showInitialSkeleton ? (
                <ProductGridSkeleton
                    ref={gridRef}
                    cells={MOB_PRODUCTS_PAGE_SIZE}
                    layoutGridClass="slider-mob-new__grid"
                />
            ) : (
                <div className="slider-mob-new__grid" ref={gridRef}>
                    {slides.map((product, index) =>
                        mobileCardVariant === 'simpleTouch' ? (
                            <SimpleTouchCarouselCard
                                key={product.id}
                                product={product}
                                gridIndex={index}
                                isActive={activeCardId != null && String(activeCardId) === String(product.id)}
                                onActivate={handleActivateCard}
                                clickStartsCarouselForTesting={mobPreviewClickActivatesCarousel}
                            />
                        ) : mobileCardVariant === 'translateStrip' ? (
                            <ProductCardTranslateCarousel
                                key={product.id}
                                product={product}
                                gridIndex={index}
                                isActive={activeCardId != null && String(activeCardId) === String(product.id)}
                                onActivate={handleActivateCard}
                                clickStartsCarouselForTesting={mobPreviewClickActivatesCarousel}
                            />
                        ) : (
                            <ProductCardCarouselStack
                                key={product.id}
                                product={product}
                                gridIndex={index}
                                isActive={activeCardId != null && String(activeCardId) === String(product.id)}
                                onActivate={handleActivateCard}
                            />
                        )
                    )}
                </div>
            )}
            {loading && (
                <div className="slider-mob-new__loader-wrap" aria-live="polite" aria-busy="true">
                    <div className="slider-mob-new__loader" />
                    <span className="slider-mob-new__loader-text">Loading more…</span>
                </div>
            )}
            <div
                ref={lastProductRef}
                className="slider-mob-new__sentinel"
                aria-hidden
            />
            <div className="slider-mob-new__filter-hider" aria-hidden />
        </div>
    );
}
